import axios, { type AxiosResponse } from "axios";
import Customer, { type ICustomer } from "./Customer";
import CustomerWishlistItem, {
  type ICustomerWishlistItem,
} from "./CustomerWishlistItem";
import { Jsona } from "jsona";
import ProductImage from "./ProductImage";

export interface ICustomerWishlist {
  id: string | null;
  name: string | null;
  created_at: string | null;
  customer?: ICustomer;
  items: ICustomerWishlistItem[];
}

export default class CustomerWishlist implements ICustomerWishlist {
  public type: string = "customer-wishlists";
  public relationshipNames: string[] = ["customer", "items"];

  public id: string | null = null;
  public name: string | null = null;
  public created_at: string | null = null;
  public customer?: Customer | undefined;
  public items: CustomerWishlistItem[] = [];
  private static x = 0;
  public static test() {
    this.x++;
    console.log(this.x);
  }

  public constructor(data?: ICustomerWishlist) {
    if (data) {
      this.setData(data);
    }
  }

  public setData({ id, name, created_at, customer, items }: ICustomerWishlist) {
    this.id = id || null;
    this.name = name || null;
    this.created_at = created_at || null;
    this.customer = customer ? new Customer(customer) : undefined;
    this.items = Array.isArray(items)
      ? CustomerWishlistItem.fromArray(items)
      : [];
  }

  public static buildCommonUrl(
    path: string,
    withoutInclusion: boolean = false,
  ): string {
    let fields = "fields[customer-wishlists]=name";
    let include = "";
    if (!withoutInclusion) {
      const sizeFields = ProductImage.getAllSizeFields().join(",");
      fields += ",items";
      fields += "&fields[customer-wishlist-items]=qty,product";
      fields +=
        "&fields[products]=name,slug,coverImage,price_with_tax,current_price_with_tax,mwi_one_disc_percent,mwi_one_price_with_tax";
      fields += `&fields[product-images]=is_cover,${sizeFields}`;
      include = "include=items.product.coverImage";
    }
    return path + "?" + fields + "&" + include;
  }

  public async save(): Promise<CustomerWishlist> {
    const jsona = new Jsona();
    const data = jsona.serialize({ stuff: this, includeNames: [] });
    let res: AxiosResponse;
    if (this.id) {
      const path = `/shop-v1/customer-wishlists/${this.id}`;
      res = await axios.patch(CustomerWishlist.buildCommonUrl(path), data);
    } else {
      const path = `/shop-v1/customer-wishlists`;
      delete (data.data as { id?: string | null }).id;
      res = await axios.post(CustomerWishlist.buildCommonUrl(path), data);
    }
    if (res.status != 200 && res.status != 201) {
      throw res;
    }
    const newData = jsona.deserialize(res.data);
    this.setData(newData as ICustomerWishlist);
    return this;
  }

  public static fromArray(items: ICustomerWishlist[]) {
    return items.map((item) => new CustomerWishlist(item));
  }

  public static async getOrCreateByCustomer(
    customerId: string,
    withoutInclusion: boolean = false,
  ): Promise<CustomerWishlist[]> {
    const path = `/shop-v1/customers/${customerId}/wishlists`;
    const res = await axios.get(
      CustomerWishlist.buildCommonUrl(path, withoutInclusion),
    );
    const jsona = new Jsona();
    if (res.status != 200) {
      throw res;
    }
    const data = jsona.deserialize(res.data) || [];
    const wishlists = CustomerWishlist.fromArray(data as ICustomerWishlist[]);
    if (wishlists.length === 0) {
      const wishlist = new CustomerWishlist();
      wishlist.customer = new Customer({ id: customerId } as ICustomer);
      wishlist.name = "Mis favoritos";
      wishlist.save();
      wishlists.push(wishlist);
    }

    return wishlists;
  }

  private static wishlistsLoadedOnce: CustomerWishlist[] = [];
  private static loadedWishlistsOnce = false;
  public static async loadWishlistsOnce(
    customerId: string,
  ): Promise<CustomerWishlist[]> {
    if (this.loadedWishlistsOnce) {
      this.loadedWishlistsOnce = true;
      return this.wishlistsLoadedOnce;
    }
    this.wishlistsLoadedOnce = await this.getOrCreateByCustomer(customerId);
    return this.wishlistsLoadedOnce;
  }

  public static async clearWishlistsLoadedOnce() {
    this.loadedWishlistsOnce = false;
    this.wishlistsLoadedOnce = [];
  }
}
